import type { PaginationArguments, PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { LinksService, type CreateLinkRequestJson, type CreateLinkResponse, type DeleteLinkRequest, type Domain, type DomainFilter, type DomainStatus, type GenerateLinkResponse, type GetLinkRequest, type GetLinkResponse, type UpdateLinkRequest, type UpdateLinkResponse } from "#gen/links/v1/links_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { FieldMask } from "@bufbuild/protobuf/wkt";
import type { CallOptions, Client } from "@connectrpc/connect";

export interface DomainsList {
	items: Domain[];
	pageInfo?: PaginationPageInfo;
}

export class LinksClient {
	private client: Client<typeof LinksService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.links || "/", LinksService, options);
	}

	async listDomains(orgId: string, filter?: DomainFilter, paginate?: PaginationArguments, options?: CallOptions): Promise<DomainsList> {
		try {
			return await this.client.listDomains({ orgId, filter, paginate }, options);
		} catch (e) {
			throw handleError(e);
		}
	}

	async getDomain(domainId: string, orgId: string): Promise<Domain> {
		try {
			const res = await this.client.getDomain({ domainId, orgId });
			return res.item!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getDefaultDomain(workspaceId: string): Promise<Domain | undefined> {
		try {
			const res = await this.client.getDefaultDomain({ workspaceId });
			return res.item!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createDomain(orgId: string, domain: string, description?: string, workspaceDefault?: boolean, orgDefault?: boolean, status?: DomainStatus): Promise<Domain> {
		try {
			const res = await this.client.createDomain({
				orgId,
				domain,
				description,
				workspaceDefault,
				orgDefault,
				status,
				customHostname: true,
			});
			return res.item!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateDomain(domain: Domain, mask: FieldMask): Promise<Domain> {
		try {
			const { $typeName: _, id, ...domainElements } = domain;
			const res = await this.client.updateDomain({
				...domainElements,
				domainId: id,
				mask,
			});
			return res.item!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteDomain(orgId: string, domainId: string): Promise<void> {
		try {
			await this.client.deleteDomain({ orgId, domainId });
		} catch (e) {
			throw handleError(e);
		}
	}

	async setWorkspaceDefaultDomain(workspaceId: string, domainId: string): Promise<void> {
		try {
			await this.client.setWorkspaceDefault({ workspaceId, domainId });
		} catch (e) {
			throw handleError(e);
		}
	}

	async generateLink(orgId: string, workspaceId: string, batchId: string, domainId?: string, hash?: string): Promise<GenerateLinkResponse> {
		try {
			const res = await this.client.generateLink({ orgId, workspaceId, batchId, domainId, hash });
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getLink(req: GetLinkRequest, options?: CallOptions): Promise<GetLinkResponse> {
		try {
			const res = await this.client.getLink(req, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createLink(req: CreateLinkRequestJson, options?: CallOptions): Promise<CreateLinkResponse> {
		try {
			const res = await this.client.createLink(req, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateLink(req: UpdateLinkRequest, options?: CallOptions): Promise<UpdateLinkResponse> {
		try {
			const res = await this.client.updateLink(req, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteLink(req: DeleteLinkRequest, options?: CallOptions): Promise<void> {
		try {
			await this.client.deleteLink(req, options);
		} catch (e) {
			throw handleError(e);
		}
	}
}
